/** ເກີນຄ່ານີ້ = ຖືວ່າຄຳຕອບຄ້າຍກັນ (ສະແດງ % ແລະ ອະນຸຍາດລຶບຊ້ຳ) */
export const FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT = 70;

export type ResponseAnswerMap = Record<
  string,
  { value: string | null; fileKey: string | null }
>;

export type ResponseSimilarityInput = {
  id: string;
  submittedAt: Date | string;
  answers: ResponseAnswerMap;
};

export type ResponseSimilarityMeta = {
  maxSimilarityPercent: number;
  canDeleteAsSimilarDuplicate: boolean;
};

function toTimeMs(d: Date | string): number {
  const t = d instanceof Date ? d.getTime() : Date.parse(String(d));
  return Number.isNaN(t) ? 0 : t;
}

function canonicalCheckboxValue(raw: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return [...parsed].map(String).sort().join("\x1f");
    }
  } catch {
    /* skip */
  }
  return raw;
}

/** ຄ່າມາດຕະຖານຕໍ່ຟິວ — ໃຊ້ເທົ່າກັນທຸກຄຳຕອບ (ຂໍ້ມູນເກົ່າໃນ DB ຄິດຍ້ອນຫຼັງ) */
export function canonicalAnswerCell(
  value: string | null | undefined,
  fileKey: string | null | undefined,
): string {
  const fk = fileKey?.trim() ?? "";
  if (fk.length > 0) return `file:${fk}`;
  const v = (value ?? "").trim();
  if (v.length === 0) return "";
  if (v.startsWith("[") || v.startsWith("{")) {
    return `chk:${canonicalCheckboxValue(v)}`;
  }
  return `v:${v.toLowerCase()}`;
}

export function buildCanonicalAnswerMap(
  answers: ResponseAnswerMap,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const [fieldId, cell] of Object.entries(answers)) {
    out.set(fieldId, canonicalAnswerCell(cell?.value, cell?.fileKey));
  }
  return out;
}

const MAX_COMPARE_CHARS = 4000;

function truncateForCompare(s: string): string {
  if (s.length <= MAX_COMPARE_CHARS) return s;
  return s.slice(0, MAX_COMPARE_CHARS);
}

function levenshteinDistance(a: string, b: string): number {
  const s = truncateForCompare(a);
  const t = truncateForCompare(b);
  const m = s.length;
  const n = t.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      const del = (prev[j] ?? 0) + 1;
      const ins = (curr[j - 1] ?? 0) + 1;
      const sub = (prev[j - 1] ?? 0) + cost;
      curr[j] = Math.min(del, ins, sub);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j] ?? 0;
  }
  return prev[n] ?? 0;
}

/** 0–100 ຄວາມຄ້າຍຄືຂອງຄ່າສອງຊ່ອງ */
export function cellSimilarityPercent(a: string, b: string): number {
  if (a === b) return 100;
  if (a.length === 0 && b.length === 0) return 100;
  if (a.length === 0 || b.length === 0) return 0;
  const dist = levenshteinDistance(a, b);
  const maxLen = Math.max(a.length, b.length, 1);
  return Math.round((1 - dist / maxLen) * 100);
}

/** 0–100 ຄວາມຄ້າຍຄືລະຫວ່າງສອງຄຳຕອບ (ເຉລີຍເຊື່ອງຄ່າເກົ່າໃນ DB) */
export function responseSimilarityPercent(
  mapA: Map<string, string>,
  mapB: Map<string, string>,
): number {
  const keys = new Set([...mapA.keys(), ...mapB.keys()]);
  if (keys.size === 0) return 100;
  let sum = 0;
  for (const k of keys) {
    sum += cellSimilarityPercent(mapA.get(k) ?? "", mapB.get(k) ?? "");
  }
  return Math.round(sum / keys.size);
}

class UnionFind {
  private parent = new Map<string, string>();

  constructor(ids: string[]) {
    for (const id of ids) this.parent.set(id, id);
  }

  find(x: string): string {
    let p = this.parent.get(x) ?? x;
    while (p !== (this.parent.get(p) ?? p)) {
      p = this.parent.get(p) ?? p;
    }
    let cur = x;
    while (cur !== p) {
      const next = this.parent.get(cur) ?? cur;
      this.parent.set(cur, p);
      cur = next;
    }
    return p;
  }

  union(a: string, b: string): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(rb, ra);
  }
}

function pickKeeperId(
  ids: string[],
  byId: Map<string, ResponseSimilarityInput>,
): string {
  const first = ids[0];
  if (!first) return "";
  let keeper = first;
  for (const id of ids.slice(1)) {
    const cur = byId.get(id);
    const best = byId.get(keeper);
    if (!cur || !best) continue;
    const tCur = toTimeMs(cur.submittedAt);
    const tBest = toTimeMs(best.submittedAt);
    if (tCur < tBest || (tCur === tBest && id < keeper)) {
      keeper = id;
    }
  }
  return keeper;
}

/**
 * ຄິດ % ຄວາມຄ້າຍຄືສູງສຸດຕໍ່ຄຳຕອບອື່ນ ແລະ ສິດລຶບ (ລຶບໄດ້ຖ້າບໍ່ແມ່ນຕົວເກັບໃນກຸ່ມທີ່ຄ້າຍກັນ ≥ threshold)
 */
export function computeFormResponseSimilarityMeta(
  responses: ResponseSimilarityInput[],
  thresholdPercent: number = FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT,
): Map<string, ResponseSimilarityMeta> {
  const out = new Map<string, ResponseSimilarityMeta>();
  if (responses.length === 0) return out;

  const byId = new Map(responses.map((r) => [r.id, r]));
  const canonical = new Map(
    responses.map((r) => [r.id, buildCanonicalAnswerMap(r.answers)]),
  );

  const maxSim = new Map<string, number>();
  for (const r of responses) maxSim.set(r.id, 0);

  const uf = new UnionFind(responses.map((r) => r.id));

  for (let i = 0; i < responses.length; i++) {
    for (let j = i + 1; j < responses.length; j++) {
      const a = responses[i];
      const b = responses[j];
      if (!a || !b) continue;
      const mapA = canonical.get(a.id);
      const mapB = canonical.get(b.id);
      if (!mapA || !mapB) continue;
      const pct = responseSimilarityPercent(mapA, mapB);
      maxSim.set(a.id, Math.max(maxSim.get(a.id) ?? 0, pct));
      maxSim.set(b.id, Math.max(maxSim.get(b.id) ?? 0, pct));
      if (pct >= thresholdPercent) uf.union(a.id, b.id);
    }
  }

  const components = new Map<string, string[]>();
  for (const r of responses) {
    const root = uf.find(r.id);
    const list = components.get(root) ?? [];
    list.push(r.id);
    components.set(root, list);
  }

  const canDelete = new Map<string, boolean>();
  for (const r of responses) canDelete.set(r.id, false);

  for (const ids of components.values()) {
    if (ids.length < 2) continue;
    const keeper = pickKeeperId(ids, byId);
    if (!keeper) continue;
    for (const id of ids) {
      if (id !== keeper) canDelete.set(id, true);
    }
  }

  for (const r of responses) {
    const pct = maxSim.get(r.id) ?? 0;
    const deletable = (canDelete.get(r.id) ?? false) && pct >= thresholdPercent;
    out.set(r.id, {
      maxSimilarityPercent: pct,
      canDeleteAsSimilarDuplicate: deletable,
    });
  }

  return out;
}
